import { Component } from '@theme/component';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';
import { ProductSelectEvent } from '@shopify/events';

/**
 * CUSTOMIZATION NOTICE
 * ---------------------
 * This file is a customized version of Shopify's default `variant-picker.js`.
 * It adds two-level dependent-option filtering (#filterOptionsBasedOnSelection /
 * #applyInitialFiltering) that is NOT part of the stock theme.
 *
 * If you ever update/re-pull the base theme, this file will likely be
 * overwritten and this customization (and the stability fixes below) will be
 * lost. Keep a copy of this file, or track it in your own git branch/fork,
 * separate from the vendor theme update flow.
 */

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets - The fieldset elements.
 * @property {HTMLElement} [overflowList] - The overflow list element.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  /** @type {Array<any> | null} */
  #variantsData = null;

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      } else {
        this.#checkedIndices.push([]);
      }
    });

    this.#loadVariantsData();

    // Apply initial filtering based on URL parameters or initial selection.
    // Wrapped defensively so that a filtering issue never prevents the picker
    // itself (and the add-to-cart flow) from working.
    try {
      this.#applyInitialFiltering();
    } catch (error) {
      console.error('variant-picker: failed to apply initial dependent-option filtering', error);
    }

    this.addEventListener('change', this.variantChanged.bind(this));
    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
  }

  /**
   * Loads the variants data from the embedded JSON script.
   */
  #loadVariantsData() {
    const variantsDataScript = this.querySelector('script[data-variants-data]');
    if (variantsDataScript) {
      try {
        const data = JSON.parse(variantsDataScript.textContent);
        this.#variantsData = data.variants || [];
      } catch (error) {
        console.error('Failed to parse variants data:', error);
        this.#variantsData = [];
      }
    }
  }

  /**
   * Applies initial filtering when the component loads (or after a morph).
   * This handles the case when the page loads with a variant already selected via URL parameters,
   * and also re-syncs fieldset pill CSS after any auto-selection made during filtering.
   */
  #applyInitialFiltering() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Start from the first fieldset and apply filtering sequentially
    for (let i = 0; i < fieldsets.length - 1; i++) {
      const fieldset = fieldsets[i];
      const selectedInput = fieldset?.querySelector('input:checked, select');

      if (selectedInput) {
        this.#safeFilterOptionsBasedOnSelection(i);
      }
    }

    // Make sure pill/highlight CSS vars reflect whatever ended up checked
    // (including any fallback selection made by #filterOptionsBasedOnSelection).
    this.updateVariantPickerCss();
  }

  /**
   * Wrapper around #filterOptionsBasedOnSelection that never lets a filtering bug
   * take down the rest of the variant picker (option updates, add-to-cart, price,
   * etc). If something unexpected happens (unexpected DOM shape, missing data),
   * we log it and simply skip filtering for this change rather than throwing.
   *
   * Also fires a `variantpicker:autoselect` custom event (bubbling, non-cancelable)
   * for each fieldset where a fallback value was auto-selected, so any third-party
   * script (analytics, subscriptions apps, etc.) that cares can react to it. This
   * is purely additive — nothing in this component listens for it — so it cannot
   * change or break existing behavior.
   *
   * @param {number} changedFieldsetIndex
   */
  #safeFilterOptionsBasedOnSelection(changedFieldsetIndex) {
    try {
      const autoSelected = this.#filterOptionsBasedOnSelection(changedFieldsetIndex);

      if (autoSelected && autoSelected.length) {
        for (const { fieldsetIndex, radio } of autoSelected) {
          this.dispatchEvent(
            new CustomEvent('variantpicker:autoselect', {
              bubbles: true,
              detail: {
                fieldsetIndex,
                optionName: radio.dataset.optionName,
                value: radio.value,
                optionValueId: radio.dataset.optionValueId,
              },
            })
          );
        }
      }
    } catch (error) {
      console.error('variant-picker: failed to apply dependent-option filtering', error);
    }
  }

  /**
   * Filters the option values in fieldsets based on the selected option in the previous fieldset.
   * If the value that was previously selected in a downstream fieldset becomes invalid, this will
   * automatically select the first available valid option instead of leaving the fieldset empty
   * (an empty fieldset produces an incomplete variant selection, which is what caused the
   * "black buttons / disabled buy button until manually clicked" bug).
   *
   * @param {number} changedFieldsetIndex - The index of the fieldset that was changed.
   * @returns {Array<{fieldsetIndex: number, radio: HTMLInputElement}>} Any fallback
   *   auto-selections that were made in downstream fieldsets (empty array if none).
   */
  #filterOptionsBasedOnSelection(changedFieldsetIndex) {
    /** @type {Array<{fieldsetIndex: number, radio: HTMLInputElement}>} */
    const autoSelections = [];

    if (!Array.isArray(this.#variantsData) || this.#variantsData.length === 0) return autoSelections;

    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Only filter if we have at least 2 fieldsets and the changed fieldset is not the last one
    if (fieldsets.length < 2 || changedFieldsetIndex >= fieldsets.length - 1) return autoSelections;

    // Get the selected value from the changed fieldset. This works for both the
    // buttons/swatch style (radio inputs) and the dropdown style (a <select>).
    const changedFieldset = fieldsets[changedFieldsetIndex];
    const selectedInput = changedFieldset?.querySelector('input:checked, select');
    if (!selectedInput) return autoSelections;

    const selectedValue = selectedInput.value;

    // Determine which Shopify option position this is (option1, option2, etc).
    // Prefer the real position from data-option-position (set in the Liquid template)
    // over assuming it always equals "array index + 1" — that assumption silently
    // breaks whenever a single-value option (which isn't rendered as a fieldset at
    // all) sits before a multi-value option, shifting the array indices out of sync
    // with Shopify's actual option1/option2/option3 numbering.
    const changedPosition = changedFieldset.dataset.optionPosition || String(changedFieldsetIndex + 1);
    const optionPosition = `option${changedPosition}`;

    // Filter subsequent fieldsets
    for (let i = changedFieldsetIndex + 1; i < fieldsets.length; i++) {
      const fieldsetToFilter = fieldsets[i];
      if (!fieldsetToFilter) continue;

      const nextPosition = fieldsetToFilter.dataset.optionPosition || String(i + 1);
      const nextOptionPosition = `option${nextPosition}`;

      // Get all valid option values for this fieldset based on the selected value
      const validOptionValues = new Set();

      for (const variant of this.#variantsData) {
        if (variant[optionPosition] === selectedValue) {
          if (variant[nextOptionPosition]) {
            validOptionValues.add(variant[nextOptionPosition]);
          }
        }
      }

      const selectEl = fieldsetToFilter.querySelector('select');

      if (selectEl) {
        // --- Dropdown-style fieldset ---
        const optionEls = Array.from(selectEl.options);

        for (const optionEl of optionEls) {
          const valid = validOptionValues.has(optionEl.value);
          optionEl.hidden = !valid;
          optionEl.disabled = !valid || optionEl.dataset.optionAvailable === 'false';
        }

        const currentlySelectedOption = selectEl.options[selectEl.selectedIndex];
        if (currentlySelectedOption && !validOptionValues.has(currentlySelectedOption.value)) {
          const fallbackOption =
            optionEls.find((o) => validOptionValues.has(o.value) && o.dataset.optionAvailable !== 'false') ??
            optionEls.find((o) => validOptionValues.has(o.value));

          if (fallbackOption) {
            selectEl.value = fallbackOption.value;
            this.updateSelectedOption(selectEl);

            autoSelections.push({ fieldsetIndex: i, radio: fallbackOption });
            autoSelections.push(...this.#filterOptionsBasedOnSelection(i));
          }
        }

        continue;
      }

      // --- Buttons/swatch-style fieldset (radio inputs) ---
      const radios = this.#radios[i];
      if (!radios) continue;

      // Show/hide radio buttons based on valid values
      for (const radio of radios) {
        const radioValue = radio.value;
        const parentLabel = radio.parentElement;
        if (!parentLabel) continue;

        if (validOptionValues.has(radioValue)) {
          parentLabel.style.display = '';
          radio.disabled = radio.dataset.optionAvailable === 'false';
        } else {
          parentLabel.style.display = 'none';
          radio.disabled = true;
        }
      }

      // If the currently selected option in this fieldset is no longer valid, deselect it
      // and automatically select a valid replacement so the fieldset is never left empty.
      const currentlySelectedInFieldset = fieldsetToFilter.querySelector('input:checked');
      if (currentlySelectedInFieldset && !validOptionValues.has(currentlySelectedInFieldset.value)) {
        currentlySelectedInFieldset.checked = false;
        currentlySelectedInFieldset.dataset.currentChecked = 'false';

        const checkedIndices = this.#checkedIndices[i];
        if (checkedIndices) {
          checkedIndices.length = 0;
        }

        // Prefer an available valid option; fall back to any valid option (even sold out)
        // rather than leaving nothing selected.
        const fallbackRadio =
          radios.find((radio) => validOptionValues.has(radio.value) && radio.dataset.optionAvailable !== 'false') ??
          radios.find((radio) => validOptionValues.has(radio.value));

        if (fallbackRadio) {
          fallbackRadio.checked = true;
          fallbackRadio.dataset.currentChecked = 'true';
          fallbackRadio.disabled = fallbackRadio.dataset.optionAvailable === 'false';

          const inputIndex = Number.parseInt(fallbackRadio.dataset.inputIndex || '');
          if (!Number.isNaN(inputIndex)) {
            this.#checkedIndices[i] = [inputIndex];
          }

          autoSelections.push({ fieldsetIndex: i, radio: fallbackRadio });

          // Cascade the fallback selection to any further downstream fieldsets too.
          autoSelections.push(...this.#filterOptionsBasedOnSelection(i));
        }
      }
    }

    return autoSelections;
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);

    // Apply filtering when an option changes. This may auto-select a fallback value
    // in a downstream fieldset if the previous selection there is no longer valid.
    // NOTE: this only ever touches fieldsets AFTER the one the user just changed, so
    // `selectedOption` (derived from event.target, exactly as in the original code)
    // always remains the correct element to build the request/URL from below — we
    // deliberately do NOT swap it for "whatever is checked first in the DOM", since
    // that could point at an unrelated earlier fieldset and misattribute
    // connectedProductUrl/variantId for combined listings.
    if (event.target instanceof HTMLInputElement || event.target instanceof HTMLSelectElement) {
      const fieldsetIndex = Number.parseInt(event.target.dataset.fieldsetIndex || '');
      if (!Number.isNaN(fieldsetIndex)) {
        this.#safeFilterOptionsBasedOnSelection(fieldsetIndex);
      }
    }

    // Make sure pill/highlight CSS reflects any fallback auto-selection immediately,
    // instead of waiting for the next resize/morph cycle.
    this.updateVariantPickerCss();

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    const optionValueId = selectedOption.dataset.optionValueId ?? '';
    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), morphElementSelector, optionValueId);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   * @param {string} [optionValueId] - The selected option value ID for event detail.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector, optionValueId = '') {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const deferredEventPromise = ProductSelectEvent.createPromise();
    const selectedOptions = this.getAllSelectedOptions();

    this.dispatchEvent(
      new ProductSelectEvent({
        product: {
          id: this.dataset.productId ?? '',
          title: this.dataset.productTitle ?? '',
          handle: this.dataset.productHandle ?? '',
        },
        selectedOptions,
        detail: {
          optionValueId,
        },
        promise: deferredEventPromise.promise,
      })
    );

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const variantPickerJsonScript = html.querySelector(`variant-picker script[type="application/json"]`);
        const textContent = variantPickerJsonScript?.textContent;

        if (!textContent) {
          deferredEventPromise.resolve({
            variant: null,
            detail: {
              html,
              productId: this.dataset.productId ?? '',
              sourceId: this.selectedOptionId,
              resource: null,
            },
          });
          return;
        }

        let newProduct;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const { overflowList } = this.refs;
          const wasSwatchesExpanded =
            overflowList instanceof OverflowList && overflowList.getAttribute('disabled') === 'true';

          newProduct = this.updateVariantPicker(html);

          if (wasSwatchesExpanded) {
            const overflowListAfterMorph = overflowList;
            if (overflowListAfterMorph instanceof OverflowList) {
              overflowListAfterMorph.showAll();
            }
          }
        }

        // Resolve the ProductSelectEvent promise with all data needed by listeners
        if (this.selectedOptionId) {
          const variantData = JSON.parse(textContent);

          if (variantData && typeof variantData === 'object') {
            const productViewAttr = variantPickerJsonScript
              ?.closest('[view-event-payload]')
              ?.getAttribute('view-event-payload')
              ?.trim();

            deferredEventPromise.resolve({
              variant: (productViewAttr && JSON.parse(productViewAttr))?.product?.selectedVariant ?? null,
              detail: {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
                sourceId: this.selectedOptionId,
                resource: variantData,
              },
            });

            return;
          }
        }

        // Variant data is null/invalid (e.g. unavailable variant combination) —
        // still include detail with html so listeners can update UI (disable buttons, morph text)
        deferredEventPromise.resolve({
          variant: null,
          detail: {
            html,
            productId: this.dataset.productId ?? '',
            newProduct,
            sourceId: this.selectedOptionId,
            resource: null,
          },
        });
      })
      .catch((error) => {
        deferredEventPromise.reject(error);
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });

    // Reload variants data after morphing
    this.#loadVariantsData();

    // Re-index radios/checked-indices caches since morph may have replaced nodes,
    // then re-apply filtering (also re-syncs pill CSS, including any fallback
    // auto-selection needed after the server re-rendered the fieldsets).
    // Wrapped defensively so a filtering issue never breaks the section morph itself.
    try {
      this.#reindexRadios();
      this.#applyInitialFiltering();
    } catch (error) {
      console.error('variant-picker: failed to re-sync dependent-option filtering after morph', error);
    }

    this.updateVariantPickerCss();

    return newProduct;
  }

  /**
   * Rebuilds the cached radio/checked-index arrays from the current DOM.
   * Needed after a morph, since morph can replace/reorder fieldset nodes.
   */
  #reindexRadios() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    this.#radios = [];
    this.#checkedIndices = [];

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      this.#checkedIndices.push(initialCheckedIndex !== -1 ? [initialCheckedIndex] : []);
    });
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets all the selected options.
   * @returns {{name: string, value: string}[]} All the currently selected options.
   */
  getAllSelectedOptions() {
    /** @type {{name: string, value: string}[]} */
    const options = [];

    // For <select> elements, use .selectedOptions to get the current selection
    // (the [selected] HTML attribute only reflects the initial state, not user changes)
    for (const select of this.querySelectorAll('select')) {
      const selected = select.selectedOptions[0];
      if (selected?.dataset?.optionName) {
        options.push({ name: selected.dataset.optionName, value: selected.value });
      }
    }

    // For radio/checkbox fieldsets, :checked reflects the current state
    /** @type {NodeListOf<HTMLInputElement>} */
    const checkedInputs = this.querySelectorAll('fieldset input:checked');
    for (const input of checkedInputs) {
      if (input.dataset?.optionName) {
        options.push({ name: input.dataset.optionName, value: input.value });
      }
    }

    return options;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
